/**
 * Export CSV côté client, pour les écrans de liste dont les données sont déjà chargées en mémoire
 * (parc, trackers, documents…) — pas de génération asynchrone nécessaire ici, à la différence du
 * module `rapports/`, qui passe par une Lambda et S3 pour des exports programmés à l'avance sur des
 * volumes qui ne tiennent pas déjà dans le navigateur.
 *
 * Même convention que `backend/src/grv_platform/rapports/formats.py::en_csv`, pour qu'un fichier
 * généré ici s'ouvre exactement comme un rapport téléchargé : point-virgule (le séparateur attendu
 * par Excel en configuration régionale française — une virgule y est déjà le séparateur décimal),
 * retours à la ligne CRLF, et un BOM UTF-8 (sans lui, Excel lit le fichier en ISO-8859-1 et affiche
 * « DÃ©pÃ´t » à la place de « Dépôt »).
 */
import { api, requete } from "../api/client";
import type { Reponse } from "../api/client";

const SEPARATEUR = ";";
const RETOUR_LIGNE = "\r\n";
const BOM = "﻿";

/**
 * Récupère toutes les pages d'une liste paginée, en suivant le curseur jusqu'à épuisement.
 *
 * Pour les écrans qui, contrairement à Parc GRV ou Trackers (`docs/DECISIONS.md` D75), ne chargent
 * qu'une page en mémoire à l'écran (ex. Alertes, 50 par page) : un export « période illimitée » ne
 * peut pas se contenter des lignes déjà affichées, il doit les redemander toutes.
 */
export async function collecterToutesLesPages<T>(
  chemin: string,
  parametres: Record<string, string | number | null | undefined> = {},
): Promise<T[]> {
  const lignes: T[] = [];
  let curseur: string | null = null;
  do {
    const reponse: Reponse<T[]> = await api.lire<T[]>(
      chemin + requete({ ...parametres, cursor: curseur }),
    );
    lignes.push(...reponse.data);
    curseur = reponse.meta?.cursor ?? null;
  } while (curseur);
  return lignes;
}

export type ColonneExport<T> = {
  libelle: string;
  valeur: (ligne: T) => string | number | boolean | null | undefined;
};

function formaterCellule(valeur: string | number | boolean | null | undefined): string {
  if (valeur === null || valeur === undefined) return "";
  if (typeof valeur === "boolean") return valeur ? "oui" : "non";
  return String(valeur);
}

function echapperCellule(valeur: string): string {
  if (valeur.includes(SEPARATEUR) || valeur.includes('"') || /[\r\n]/.test(valeur)) {
    return `"${valeur.replace(/"/g, '""')}"`;
  }
  return valeur;
}

/**
 * Construit le contenu textuel du CSV, BOM compris — séparé de `telechargerCsv` pour rester
 * testable sans dépendre du DOM (`Blob`, `URL.createObjectURL`).
 */
export function construireCsv<T>(colonnes: ColonneExport<T>[], lignes: T[]): string {
  const ligneVers = (valeurs: string[]) =>
    valeurs.map((valeur) => echapperCellule(valeur)).join(SEPARATEUR);

  const corps = [
    ligneVers(colonnes.map((colonne) => colonne.libelle)),
    ...lignes.map((ligne) => ligneVers(colonnes.map((colonne) => formaterCellule(colonne.valeur(ligne))))),
  ];
  return BOM + corps.join(RETOUR_LIGNE) + RETOUR_LIGNE;
}

export function telechargerCsv<T>(
  nomFichier: string,
  colonnes: ColonneExport<T>[],
  lignes: T[],
): void {
  const contenu = construireCsv(colonnes, lignes);

  const bloc = new Blob([contenu], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(bloc);
  const lien = document.createElement("a");
  lien.href = url;
  lien.download = nomFichier;
  document.body.appendChild(lien);
  lien.click();
  document.body.removeChild(lien);
  URL.revokeObjectURL(url);
}

/**
 * Bouton d'export prêt à l'emploi : le nom du fichier porte la date du jour, pour que deux exports
 * successifs ne s'écrasent pas silencieusement dans le dossier de téléchargement.
 */
export function BoutonExporterCsv<T>({
  nomFichier,
  colonnes,
  lignes,
}: {
  nomFichier: string;
  colonnes: ColonneExport<T>[];
  lignes: T[];
}) {
  return (
    <button
      type="button"
      onClick={() => telechargerCsv(`${nomFichier}-${horodatageFichier()}.csv`, colonnes, lignes)}
      disabled={lignes.length === 0}
    >
      Télécharger (Excel)
    </button>
  );
}

export function horodatageFichier(): string {
  const maintenant = new Date();
  const partie = (valeur: number) => String(valeur).padStart(2, "0");
  return (
    `${maintenant.getFullYear()}${partie(maintenant.getMonth() + 1)}${partie(maintenant.getDate())}` +
    `-${partie(maintenant.getHours())}${partie(maintenant.getMinutes())}`
  );
}
